import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'expo-router';
import { CustomEventNames, EventTypes, logRBIEvent } from 'features/analytics';
import {
  applyAuthenticatedAdvertisingOptOut,
  applyGuestAdvertisingOptOutOnSignIn,
  applyIosTrackingDenial,
  clearSameSessionAdvertisingDenial,
  notifyIosTrackingDenialPersistenceResult,
  reconcileRetainedPendingCookieOptOutOnSignIn,
  subscribeToPlatformTrackingConsentChange,
  trackGuestTrackingConsentMerge,
} from 'features/privacy/interest-based-advertising/platform-tracking-consent';
import { updateAmplitudeUserAttributes } from 'features/profile/utils/analytics';
import { Platform } from 'react-native';

import { toaster } from 'components/toaster/toaster';
import { useExposeSignupFlowRedesignExperiment } from 'experiments/signup-flow-redesign/use-enable-signup-flow-redesign-experiment';
import { GetMeDocument, useUpdateMeMutation } from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import {
  AuthInterface,
  ModalAuthScreen,
  ModalAuthState,
  ModalAuthTrigger,
  OpenSignInModalProps,
} from 'state/auth/types';
import { t } from 'state/intl/translations';
import { APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS } from 'utils/constants';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';
import { TraceName, startTrace } from 'utils/performance/trace';

import { useAccountAuthentication } from './hooks/use-account-authentication';
import { UserDetails, _useCurrentUser } from './hooks/use-current-user';

// Initial state for the modal authentication hook.
// This defines the default values for all modal properties when the component mounts or when the modal is explicitly closed.
const initialModalAuthState: ModalAuthState = {
  screen: ModalAuthScreen.CLOSED,
  shouldDisplaySignInSuccessMessage: true,
  shouldDisableHeadlineLinks: false,
  shouldSkipDefaultSignInCallback: false,
  shouldWaitLoginForVendor: false,
  disableEmailInput: false,
  loginForVendorIsLoading: false,
  trigger: ModalAuthTrigger.Global,
  onClose: undefined,
  onPreSignIn: undefined,
  onSignInSuccess: undefined,
  user: undefined,
  providerType: undefined,
  errorMessage: '',
  heading: '',
};

export const AuthContext = createContext<AuthInterface>(({} as any) as AuthInterface);
export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [modalAuthState, setModalAuthState] = useState<ModalAuthState>(initialModalAuthState);
  const [isReAuthenticating, setIsReAuthenticating] = useState(false);
  const [otpAuthError, setOtpAuthError] = useState(null);
  const modalAuthIsOpen = modalAuthState.screen !== ModalAuthScreen.CLOSED;
  const [accountCallbackUrl, setAccountCallbackUrl] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const router = useRouter();

  useExposeSignupFlowRedesignExperiment(modalAuthState.screen === ModalAuthScreen.SIGN_UP);

  /**
   * A handler function to update the modal authentication state.
   * It merges the new state with the previous state, ensuring that no data is lost
   * and only specified properties are updated.
   *
   * @param {Partial<ModalAuthState>} newState - The new state to be merged with the previous state.
   */
  const handleSetModalAuthState = useCallback((newState: Partial<ModalAuthState>) => {
    setModalAuthState(prevModalAuthState => {
      const mergedState = { ...prevModalAuthState, ...newState } as ModalAuthState;

      const prevUser = prevModalAuthState.user || {};
      const newUser = newState.user || {};
      mergedState.user = {
        name: newUser.name ?? prevUser.name,
        email: newUser.email ?? prevUser.email,
      };

      if (JSON.stringify(prevModalAuthState) === JSON.stringify(mergedState)) {
        return prevModalAuthState;
      }

      if (newState.screen && prevModalAuthState.screen !== newState.screen) {
        logRBIEvent({
          name: CustomEventNames.AUTH_DRAWER_VIEW,
          type: EventTypes.Other,
          attributes: {
            screen: newState.screen,
          },
        });
      }
      return mergedState;
    });
  }, []);

  const openSignInModal = useCallback(
    (params?: OpenSignInModalProps) => {
      startTrace(TraceName.AUTH_MODAL_LOAD);
      const {
        errorMessage = '',
        shouldDisableHeadlineLinks = false,
        shouldWaitLoginForVendor = false,
        navigateOnSuccessRoute,
        trigger = ModalAuthTrigger.Global,
        onClose,
      } = params || {};

      handleSetModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        shouldDisplaySignInSuccessMessage: true,
        navigateOnSuccessRoute,
        errorMessage,
        shouldDisableHeadlineLinks,
        shouldWaitLoginForVendor,
        trigger,
        // Only set onClose if it's provided to avoid overriding the default close handler
        ...(onClose ? { onClose } : {}),
      });
    },
    [handleSetModalAuthState]
  );

  const onConfirmErrorDialog = useCallback(() => {
    openSignInModal();
  }, [openSignInModal]);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    onConfirm: onConfirmErrorDialog,
    modalAppearanceEventMessage: 'Error: Auth Error',
  });

  const {
    refetchCurrentUser,
    refreshCurrentUser,
    transitionCurrentUser,
    updateUserCommPrefs,
    updateUserFavStores,
    updateUserInfo,
    saveDeliveryAddress,
    editDeliveryAddress,
    removeDeliveryAddress,
    user,
    userLoading,
    useUpdateMeMutationLoading,
    isAuthenticated,
    authTransition,
  } = _useCurrentUser();

  const {
    authLoading,
    originLocation,
    setOriginLoc,
    signIn,
    signOut,
    signUp,
    socialLogin,
    validateLogin,
    validateLoginOtp,
  } = useAccountAuthentication({
    refreshCurrentUser,
    openErrorDialog,
    transitionCurrentUser,
    setModalAuthState: handleSetModalAuthState,
    modalAuthIsOpen,
  });

  useEffect(() => {
    // save redirect url so user can get redirected correctly after login
    syncStorage.setObject(SyncStorageKey.AUTH_REDIRECT, { callbackUrl: originLocation });
  }, [originLocation]);

  const loading = authLoading || userLoading;

  const defaultCloseAuthModal = useCallback(() => {
    handleSetModalAuthState(initialModalAuthState);
  }, [handleSetModalAuthState]);

  // ADR-0002 rule 1 (guest -> authenticated merge): web-only, since OneTrust's cookie is the
  // only pre-auth "guest" tracking-consent signal - ATT/GAID have no such pre-auth moment.
  // A rejection made in this browser session overrides the account. A cookie that is already
  // off on another browser does not: the account value is what that browser receives.
  // An accepted or missing cookie never opts the account in.
  const [updateMeMutation] = useUpdateMeMutation({
    refetchQueries: [{ query: GetMeDocument }],
    awaitRefetchQueries: true,
  });
  const pendingAdvertisingOptOutWrite = useRef<{
    identityKey: string;
    promise: Promise<void>;
  } | null>(null);
  const advertisingOptOutIdentityKey = `${authTransition.epoch}:${user?.cognitoId ?? 'guest'}`;
  const currentAdvertisingOptOutIdentityKey = useRef(advertisingOptOutIdentityKey);
  currentAdvertisingOptOutIdentityKey.current = advertisingOptOutIdentityKey;

  const persistAdvertisingOptOut = useCallback(async () => {
    if (pendingAdvertisingOptOutWrite.current?.identityKey === advertisingOptOutIdentityKey) {
      return pendingAdvertisingOptOutWrite.current.promise;
    }

    const identityKey = advertisingOptOutIdentityKey;
    const pendingWrite = (async () => {
      await updateMeMutation({
        variables: { input: { adsPersonalizationOptOut: true } },
      });
      updateAmplitudeUserAttributes({ adsPersonalizationOptOut: true });
      if (currentAdvertisingOptOutIdentityKey.current === identityKey) {
        clearSameSessionAdvertisingDenial();
      }
    })();

    pendingAdvertisingOptOutWrite.current = { identityKey, promise: pendingWrite };
    try {
      await pendingWrite;
    } finally {
      if (pendingAdvertisingOptOutWrite.current?.promise === pendingWrite) {
        pendingAdvertisingOptOutWrite.current = null;
      }
    }
  }, [advertisingOptOutIdentityKey, updateMeMutation]);

  const mergeGuestTrackingConsentOnSignIn = useCallback(async () => {
    if (Platform.OS !== 'web') {
      return;
    }

    try {
      await applyGuestAdvertisingOptOutOnSignIn(persistAdvertisingOptOut);
    } catch (error) {
      logger.error({ error, message: 'Error merging guest tracking consent on sign-in' });
    }
  }, [persistAdvertisingOptOut]);

  // A cookie opt-out is written to the profile only once the auth session exists, so the
  // user-service call is not sent before it can succeed. A banner opt-in is not written.
  const cognitoId = user?.cognitoId;
  const adsPersonalizationOptOut = user?.details?.adsPersonalizationOptOut ?? false;
  useEffect(() => {
    if (Platform.OS !== 'web' || !isAuthenticated || !cognitoId) {
      return;
    }
    reconcileRetainedPendingCookieOptOutOnSignIn(cognitoId);
  }, [cognitoId, isAuthenticated]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !isAuthenticated || !cognitoId) {
      return;
    }

    let cancelled = false;
    void applyAuthenticatedAdvertisingOptOut(!adsPersonalizationOptOut, async () => {
      if (cancelled) {
        return;
      }
      await persistAdvertisingOptOut();
    }).catch(error => {
      logger.error({
        error,
        message: 'Error saving cookie advertising opt-out on the account',
      });
    });

    return () => {
      cancelled = true;
    };
  }, [adsPersonalizationOptOut, cognitoId, isAuthenticated, persistAdvertisingOptOut]);

  // Ask App Not to Track opts the signed-in profile out. Allowing tracking does not opt it in.
  useEffect(() => {
    if (Platform.OS !== 'ios' || !isAuthenticated || !cognitoId) {
      return;
    }

    let cancelled = false;
    const persistOptOut = async () => {
      if (cancelled) {
        return;
      }
      await persistAdvertisingOptOut();
      notifyIosTrackingDenialPersistenceResult(true);
    };

    void applyIosTrackingDenial(!adsPersonalizationOptOut, persistOptOut).catch(error => {
      notifyIosTrackingDenialPersistenceResult(false);
      logger.error({ error, message: 'Error saving iOS tracking denial on the account' });
    });

    const unsubscribe = subscribeToPlatformTrackingConsentChange(state => {
      if (state !== 'denied') {
        return;
      }
      void applyIosTrackingDenial(!adsPersonalizationOptOut, persistOptOut).catch(error => {
        notifyIosTrackingDenialPersistenceResult(false);
        logger.error({ error, message: 'Error saving iOS tracking denial on the account' });
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [adsPersonalizationOptOut, cognitoId, isAuthenticated, persistAdvertisingOptOut]);

  const defaultOnSignInSuccess = useCallback(() => {
    if (modalAuthState.shouldSkipDefaultSignInCallback) {
      return;
    }
    const shouldShowToast = !!modalAuthState.shouldDisplaySignInSuccessMessage;

    if (!modalAuthState.navigateOnSuccessRoute && shouldShowToast) {
      toaster.positive(t('signInSuccess'));
    }

    if (modalAuthState.navigateOnSuccessRoute?.length) {
      if (shouldShowToast) {
        toaster.positive(t('signInSuccess'));
      }
      defaultCloseAuthModal();
      // native base modal can interfere with navigation so wait till its closed to transition
      setTimeout(() => {
        router.navigate(modalAuthState.navigateOnSuccessRoute as string);
      }, APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS);
      return;
    }
    defaultCloseAuthModal();
  }, [defaultCloseAuthModal, modalAuthState, router]);

  const authContext = {
    // Computed values
    loading,
    useUpdateMeMutationLoading,
    originLocation,
    otpAuthError,
    user: user as UserDetails,
    // Functions
    refetchCurrentUser,
    refreshCurrentUser,
    isAuthenticated,
    authTransition: {
      status: authTransition.status,
      reason: authTransition.reason,
      epoch: authTransition.epoch,
    },
    signIn,
    signOut,
    signUp,
    socialLogin,
    updateUserInfo,
    saveDeliveryAddress,
    removeDeliveryAddress,
    editDeliveryAddress,
    updateUserCommPrefs,
    updateUserFavStores,
    validateLogin,
    validateLoginOtp,
    setOriginLocation: setOriginLoc as (args: null | string) => void,
    setOtpAuthError: setOtpAuthError as (args: null | string) => void,
    setModalAuthState: handleSetModalAuthState,
    modalAuthIsOpen,
    modalAuthState,
    isReAuthenticating,
    setIsReAuthenticating,
    openSignInModal,
    onSignInSuccess: useCallback(() => {
      const merge = mergeGuestTrackingConsentOnSignIn();
      // Account Info waits on this promise before it can write the account back onto cookies.
      trackGuestTrackingConsentMerge(merge);
      void merge;
      defaultOnSignInSuccess();
      modalAuthState?.onSignInSuccess && modalAuthState.onSignInSuccess();
    }, [defaultOnSignInSuccess, mergeGuestTrackingConsentOnSignIn, modalAuthState]),
    closeModalAuth: useCallback(() => {
      defaultCloseAuthModal();
      modalAuthState?.onClose && modalAuthState.onClose();
    }, [defaultCloseAuthModal, modalAuthState?.onClose]),
    accountCallbackUrl,
    setAccountCallbackUrl,
    alertMessage,
    setAlertMessage,
  };

  return (
    <AuthContext.Provider value={authContext}>
      {children}
      <ErrorDialog />
    </AuthContext.Provider>
  );
};

export default AuthContext.Consumer;
